/**
 * TRACK 2 — Send to Foreman.
 *
 * One button. The operator never assembles a PDF, never writes an email,
 * never composes a message. The component:
 *
 *  1. Builds a deep-link into ForgedOps that opens the exact target
 *     (sheet + focused object) the operator was looking at.
 *  2. Calls `navigator.share()` (iOS / Android native share sheet) when
 *     available — operator picks the foreman from their existing contacts.
 *  3. Falls back to clipboard copy for desktop, with a plain-English toast.
 *
 * Designed to be embedded next to any actionable object (measurement,
 * markup, pin, photo, revision, finding). This sprint wires it into the
 * Measurement History Drawer. The same component drops into the other
 * surfaces with a one-line addition each.
 */
import { Send } from "lucide-react";
import { confirm } from "@/lib/confirm";

function _absoluteUrl(path) {
  try {
    return new URL(path, window.location.origin).toString();
  } catch {
    return path;
  }
}

export function SendToForeman({
  target = {},      // { kind: "measurement", id, sheet_id, label, value? }
  className = "",
  size = "sm",
}) {
  const onClick = async (e) => {
    e.stopPropagation();
    const sheetId = target.sheet_id;
    const focus = target.id ? `${target.kind}:${target.id}` : null;
    const path = focus
      ? `/sheet/${sheetId}?focus=${encodeURIComponent(focus)}`
      : `/sheet/${sheetId}`;
    const url = _absoluteUrl(path);
    const human = _summary(target);

    const sharePayload = {
      title: "ForgedOps — field item",
      text: human,
      url,
    };

    if (navigator.share) {
      try {
        await navigator.share(sharePayload);
        confirm.shareSent();
        return;
      } catch (err) {
        // user cancelled — do not error
        if (err?.name === "AbortError") return;
      }
    }

    // Desktop fallback: copy a plain-text package to the clipboard.
    const payload = `${human}\n${url}`;
    try {
      await navigator.clipboard.writeText(payload);
      confirm.raw.success("✓ Copied — paste into text or email", {
        duration: 3000,
        description: human,
      });
    } catch {
      confirm.saveFailed("Couldn't copy the link", {
        recovery: "Long-press the URL on your screen and copy manually.",
      });
    }
  };

  const padding = size === "icon" ? "p-1.5" : "px-2 py-1";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-md text-[11px] font-mono uppercase tracking-wider text-[var(--fop-primary)] hover:text-[var(--fop-primary-hover)] hover:bg-[var(--fop-primary-soft)] transition-colors ${padding} ${className}`}
      data-testid={`send-to-foreman-${target.kind || "item"}`}
      aria-label="Send to foreman"
      title="Send to foreman"
    >
      <Send size={12} strokeWidth={2.5} />
      {size !== "icon" && <span>Send</span>}
    </button>
  );
}

function _summary(target) {
  const { kind, label, value } = target;
  if (kind === "measurement") {
    return label ? `Measurement · ${label}` : "Measurement on the plan";
  }
  if (kind === "markup") return label ? `Markup · ${label}` : "Markup on the plan";
  if (kind === "pin") return label ? `Field pin · ${label}` : "Field pin";
  if (kind === "photo") return "Field photo on the plan";
  if (kind === "revision") return label ? `Revision · ${label}` : "Revision change";
  return "Item on the plan";
}

export default SendToForeman;
